import React, { useState, useEffect } from 'react';
import { Layout, Menu, Avatar, Button, Typography, Drawer, Grid, Badge } from 'antd';
import { useNavigate, useLocation } from 'react-router-dom';
import './NewLayout.css'; // Import CSS pour les icônes
import {
  DashboardOutlined,
  FileTextOutlined,
  LineChartOutlined,
  ShoppingCartOutlined,
  FileSearchOutlined,
  CustomerServiceOutlined,
  TagOutlined,
  BellOutlined,
  SettingOutlined,
  LogoutOutlined,
  MenuUnfoldOutlined,
  MenuFoldOutlined,
  UserAddOutlined,
  BuildOutlined,
  AppstoreOutlined,
  CommentOutlined,
  SendOutlined,
  QrcodeOutlined,
  ScheduleOutlined,
  QuestionCircleOutlined,
  TeamOutlined,
  ToolOutlined,
  PhoneOutlined,
  GiftOutlined,
  CodeOutlined,
  FilePdfOutlined,
  SafetyCertificateOutlined,
} from '@ant-design/icons';
import { useAuth } from '../../contexts/AuthContext';
import { getImageUrl } from '../../services/uploadService';
import NotificationBell from '../Notifications/NotificationBell';
import ChatProvider from '../Chat/ChatProvider';
import ErrorBoundary from '../Common/ErrorBoundary';
import { useChatNotifications } from '../../hooks/useChatNotifications';
import { chatService } from '../../services/chatService';

const { Header, Sider, Content } = Layout;
const { Text } = Typography;
const { useBreakpoint } = Grid;

interface NewLayoutProps {
  children: React.ReactNode;
}

const NewLayout: React.FC<NewLayoutProps> = ({ children }) => {
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser, logout, avatarTimestamp } = useAuth();
  // super_admin voit tout ; admin/manager/agent voient selon leur rôle
  const isSuperAdmin = currentUser?.role === 'super_admin';
  const isAdmin = ['admin', 'super_admin', 'manager'].includes(currentUser?.role ?? '');
  // isAdmin = true pour les rôles qui accèdent aux items adminOnly (agent exclut ces items)
  
  // 🔌 Connexion Socket.IO globale
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token && currentUser) {
      console.log('🔌 [NewLayout] Initialisation connexion Socket.IO globale pour utilisateur:', currentUser.id);
      chatService.connect(token, currentUser.id.toString());
    }
  }, [currentUser]);
  
  // Hook pour le compteur de messages non lus (super_admin, managers et agents selon acces_dashboard.md - admin KO)
  const canAccessChat = ['super_admin', 'manager', 'agent'].includes(currentUser?.role ?? '');
  const { totalUnreadCount } = useChatNotifications(canAccessChat);
  
  useEffect(() => {
    console.log('🔔 [NewLayout] Total messages non lus:', totalUnreadCount, 'canAccessChat:', canAccessChat);
  }, [totalUnreadCount, canAccessChat]);

  // État du sidebar
  const [collapsed, setCollapsed] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('sidebarCollapsed');
      if (saved !== null) {
        return JSON.parse(saved);
      }
    }
    return isMobile; // Collapsé sur mobile par défaut
  });

  // Mobile drawer
  const [drawerVisible, setDrawerVisible] = useState(false);

  // Sauvegarde l'état du sidebar
  const toggleCollapsed = () => {
    const newState = !collapsed;
    setCollapsed(newState);
    localStorage.setItem('sidebarCollapsed', JSON.stringify(newState));
  };

  // Menu items configuration (selon la matrice de permissions acces_dashboard.md)
  const menuItems = [
    { key: '/dashboard', label: 'Tableau de bord', icon: <DashboardOutlined />, adminOnly: false },
    { key: '/analytics', label: 'Analytics', icon: <LineChartOutlined />, adminOnly: true },
    { key: '/utilisateurs', label: 'Utilisateurs', icon: <UserAddOutlined />, adminOnly: false },
    { key: '/clients', label: 'Clients', icon: <TeamOutlined />, adminOnly: false },
    { key: '/maintenance-offers', label: "Nos Offres et Souscriptions", icon: <BuildOutlined />, adminOnly: false },
    { key: '/techniciens', label: 'Techniciens', icon: <ToolOutlined />, adminOnly: false },
    { key: '/produits', label: 'Produits', icon: <AppstoreOutlined />, adminOnly: true },
    { key: '/splits', label: 'Splits (QR)', icon: <QrcodeOutlined />, adminOnly: true, superAdminOnly: true },
    { key: '/interventions', label: 'Interventions', icon: <FileTextOutlined />, adminOnly: false },
    // { key: '/depannage', label: 'Dépannage', icon: <BuildOutlined />, adminOnly: false },
    { key: '/rapports-interventions', label: 'Rapports Interventions', icon: <FileSearchOutlined />, adminOnly: false },
    { key: '/diagnostic-reports', label: 'Rapports de Diagnostic', icon: <FileTextOutlined />, adminOnly: false },
    // { key: '/contrats', label: 'Contrats', icon: <ShopOutlined />, adminOnly: false },
    { key: '/contrats-programmes', label: 'Contrats Programmés', icon: <ScheduleOutlined />, adminOnly: false },
    { key: '/commandes', label: 'Commandes', icon: <ShoppingCartOutlined />, adminOnly: false },
    { key: '/devis', label: 'Devis', icon: <FileSearchOutlined />, adminOnly: false },
    { key: '/reclamations', label: 'Réclamations', icon: <CustomerServiceOutlined />, adminOnly: false },
    { key: '/demandes-rappel', label: 'Demandes de rappel', icon: <PhoneOutlined />, adminOnly: false },
    { key: '/promotions', label: 'Promotions', icon: <TagOutlined />, adminOnly: true },
    { key: '/parrainage', label: 'Parrainage', icon: <GiftOutlined />, adminOnly: false },
    { key: '/collaborateurs-mct', label: 'Collaborateurs MCT', icon: <SafetyCertificateOutlined />, adminOnly: true, superAdminOnly: true },
    { key: '/qr-codes', label: 'Campagnes QR Codes', icon: <QrcodeOutlined />, adminOnly: true, superAdminOnly: true },
    { key: '/chat', label: 'Chat', icon: <Badge count={totalUnreadCount} size="small" offset={[2, -2]} showZero={false}><CommentOutlined /></Badge>, adminOnly: false },
    { key: '/broadcast-notification', label: 'Envoyer Notification', icon: <SendOutlined />, adminOnly: true },
    { key: '/notifications', label: 'Notifications', icon: <BellOutlined />, adminOnly: false },
    { key: '/system-logs', label: 'Logs Système', icon: <CodeOutlined />, adminOnly: true, superAdminOnly: true },
    { key: '/parametres', label: 'Paramètres', icon: <SettingOutlined />, adminOnly: true, superAdminOnly: true },
    { key: '/support', label: 'Support', icon: <QuestionCircleOutlined />, adminOnly: false, external: true, externalUrl: '/support.html' },
  ];

  const filteredMenuItems = menuItems
    // adminOnly : masqué pour les agents (visibles pour admin, super_admin, manager)
    // superAdminOnly : masqué pour tout le monde sauf super_admin
    // chat : réservé aux rôles autorisés (admin, super_admin, manager, agent)
    .filter(item => {
      if (item.key === '/chat') return canAccessChat;
      if ((item as any).superAdminOnly) return isSuperAdmin;
      if (item.adminOnly) return isAdmin;
      return true;
    })
    .map(item => ({
      key: item.key,
      icon: item.icon,
      label: item.label,
      onClick: () => {
        if ((item as any).external) {
          window.open((item as any).externalUrl || '/support.html', '_blank');
        } else {
          navigate(item.key);
        }
        if (isMobile) setDrawerVisible(false);
      }
    }));

  const pageTitle = {
    '/dashboard': 'Tableau de bord',
    '/analytics': 'Analytics & Reporting',
    '/interventions': 'Gestion des Interventions',
    // '/depannage': 'Gestion des Dépannages',
    // '/contrats': 'Gestion des Contrats',
    '/clients': 'Gestion des Clients',
    '/contrats-programmes': 'Contrats de Maintenance Programmée',
    '/devis': 'Devis',
    '/notifications': 'Notifications',
    '/broadcast-notification': 'Envoyer une Notification',
    '/chat': 'Conversations',
    '/system-logs': 'Console de Logs & Observabilité',
    '/commandes': 'Commandes',
    '/promotions': 'Promotions',
    '/produits': 'Produits',
    '/reclamations': 'Réclamations',
    '/rapports-interventions': "Rapports d'Interventions Techniciens",
    '/diagnostic-reports': 'Rapports de Diagnostic',
    '/parametres': 'Paramètres',
    '/parrainage': 'Gestion du Parrainage',
    '/collaborateurs-mct': 'Programme Collaborateurs MCT & Ambassadeurs',
    '/utilisateurs': 'Gestion des Utilisateurs',
    '/techniciens': 'Gestion des Techniciens',
    '/maintenance-offers': "Nos Offres et Souscriptions"
  }[location.pathname] || 'Smart Maintenance';

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const sidebarContent = (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Logo */}
      <div 
        style={{ 
          padding: collapsed && !isMobile ? '20px 12px' : '16px', 
          textAlign: 'center',
          borderBottom: '1px solid #f0f0f0',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '80px'
        }}
        onClick={() => navigate('/dashboard')}
      >
        <img
          src="/logo-maintenance.png"
          alt="Smart Maintenance Logo"
          style={{
            width: 'auto',
            height: collapsed && !isMobile ? 56 : 60,
            maxWidth: collapsed && !isMobile ? '56px' : '220px',
            objectFit: 'contain',
            transition: 'all 0.3s ease'
          }}
        />
      </div>

      {/* Menu */}
      <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>
        <Menu
          mode="inline"
          selectedKeys={[location.pathname]}
          items={filteredMenuItems}
          style={{ border: 'none', fontSize: '15px' }}
          inlineIndent={20}
        />
      </div>

      {/* Logout Button */}
      <div style={{ padding: '16px', borderTop: '1px solid #f0f0f0' }}>
        <Button
          type="text"
          danger
          icon={<LogoutOutlined />}
          onClick={handleLogout}
          block={!collapsed || isMobile}
        >
          {(!collapsed || isMobile) && 'Déconnexion'}
        </Button>
      </div>
    </div>
  );

  return (
    <Layout style={{ minHeight: '100vh', maxWidth: '100vw' }}>
      {/* Desktop Sidebar */}
      {!isMobile && (
        <Sider
          collapsible
          collapsed={collapsed}
          onCollapse={toggleCollapsed}
          trigger={null}
          width={256}
          collapsedWidth={64}
          style={{
            overflow: 'auto',
            height: '100vh',
            position: 'fixed',
            left: 0,
            top: 0,
            bottom: 0,
            background: '#fff',
            boxShadow: '2px 0 8px rgba(0,0,0,0.1)'
          }}
        >
          {sidebarContent}
        </Sider>
      )}

      {/* Mobile Drawer */}
      {isMobile && (
        <Drawer
          placement="left"
          onClose={() => setDrawerVisible(false)}
          open={drawerVisible}
          closable={false}
          width={256}
          bodyStyle={{ padding: 0 }}
        >
          {sidebarContent}
        </Drawer>
      )}

      <Layout style={{ marginLeft: isMobile ? 0 : (collapsed ? 64 : 256), transition: 'margin-left 0.2s' }}>
        {/* Header */}
        <Header
          style={{
            background: '#fff',
            padding: '0 24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            position: 'sticky',
            top: 0,
            zIndex: 100
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <Button
              type="text"
              icon={isMobile ? <MenuUnfoldOutlined /> : (collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />)}
              onClick={() => isMobile ? setDrawerVisible(true) : toggleCollapsed()}
              style={{ marginRight: 16 }}
            />
            <Text
              strong
              style={{
                fontSize: isMobile ? '16px' : '20px',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}
            >
              {pageTitle}
            </Text>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 8 : 16 }}>
            {!isMobile && (
              <Text type="secondary">
                Bonjour, {
                  currentUser?.first_name && currentUser?.last_name
                    ? `${currentUser.first_name} ${currentUser.last_name}`
                    : currentUser?.name || 'Admin'
                }
              </Text>
            )}
            <NotificationBell />
            <Avatar
              size={isMobile ? 32 : 40}
              src={currentUser?.profile_image ? `${getImageUrl(currentUser.profile_image)}?t=${avatarTimestamp}` : undefined}
            >
              {(!currentUser?.profile_image && (
                currentUser?.first_name?.charAt(0) ||
                currentUser?.name?.charAt(0) ||
                'A'
              ))}
            </Avatar>
          </div>
        </Header>

        {/* Content */}
        <Content style={{ 
          margin: isMobile ? '12px' : '16px', 
          minHeight: 'calc(100vh - 112px)',
          maxWidth: '100%',
          overflow: 'visible'
        }}>
          <div style={{ maxWidth: '100%' }}>
            {children}
          </div>
        </Content>
      </Layout>

      {/* Chat flottant isolé dans un ErrorBoundary */}
      <ErrorBoundary fallback={null}>
        <ChatProvider />
      </ErrorBoundary>
    </Layout>
  );
};

export default NewLayout;
